import { lazy, Suspense } from 'react';
import SeoHead from '../../components/SeoHead';
import Hero from './components/Hero';

const About = lazy(() => import('./components/About'));
const WhoWeServe = lazy(() => import('./components/WhoWeServe'));
const Pricing = lazy(() => import('./components/Pricing'));
const MeetTheTeam = lazy(() => import('./components/MeetTheTeam'));
const InterestTracker = lazy(() => import('./components/InterestTracker'));
const FacilityForm = lazy(() => import('./components/FacilityForm'));
const PrivatePayForm = lazy(() => import('./components/PrivatePayForm'));
const Waitlist = lazy(() => import('./components/Waitlist'));
const ContactUs = lazy(() => import('./components/ContactUs'));
const Footer = lazy(() => import('./components/Footer'));
const NotifyMe = lazy(() => import('./components/NotifyMe'));

function SectionFallback() {
  return (
    <div className="w-full py-24 flex items-center justify-center bg-slate-900">
      <div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
}

export default function HomePage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["MedicalBusiness", "LocalBusiness"],
        "@id": `${import.meta.env.VITE_SITE_URL}/#business`,
        "name": "Guardian Veterans LLC",
        "legalName": "Guardian Veterans LLC",
        "description": "Guardian Veterans provides reliable wheelchair and ambulatory medical transportation for assisted living communities, dialysis clinics, and private patients in Sun City, Peoria, Surprise, Goodyear, and West Valley Arizona. Veteran-owned NEMT service launching soon.",
        "url": `${import.meta.env.VITE_SITE_URL}/`,
        "telephone": "+16022956626",
        "email": "info@gvnemt.com",
        "priceRange": "$$",
        "currenciesAccepted": "USD",
        "paymentAccepted": "Cash, Credit Card, Insurance",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "9117 N 181st Ave",
          "addressLocality": "Waddell",
          "addressRegion": "AZ",
          "postalCode": "85355",
          "addressCountry": "US"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 33.65,
          "longitude": -112.47
        },
        "hasMap": "https://maps.google.com/?q=9117+N+181st+Ave+Waddell+AZ+85355",
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "06:30",
            "closes": "17:30"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Saturday"],
            "opens": "06:30",
            "closes": "17:30"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Sunday"],
            "opens": "06:30",
            "closes": "17:30"
          }
        ],
        "areaServed": [
          { "@type": "City", "name": "Sun City", "containedInPlace": { "@type": "State", "name": "Arizona" } },
          { "@type": "City", "name": "Peoria", "containedInPlace": { "@type": "State", "name": "Arizona" } },
          { "@type": "City", "name": "Surprise", "containedInPlace": { "@type": "State", "name": "Arizona" } },
          { "@type": "City", "name": "Goodyear", "containedInPlace": { "@type": "State", "name": "Arizona" } },
          { "@type": "City", "name": "West Valley", "containedInPlace": { "@type": "State", "name": "Arizona" } },
          { "@type": "City", "name": "Scottsdale", "containedInPlace": { "@type": "State", "name": "Arizona" } },
          { "@type": "City", "name": "Waddell", "containedInPlace": { "@type": "State", "name": "Arizona" } },
          { "@type": "City", "name": "Avondale", "containedInPlace": { "@type": "State", "name": "Arizona" } },
          { "@type": "City", "name": "Glendale", "containedInPlace": { "@type": "State", "name": "Arizona" } }
        ],
        "serviceType": [
          "Non-Emergency Medical Transportation",
          "Wheelchair Transportation",
          "Ambulatory Transportation",
          "Dialysis Transportation",
          "Hospital Discharge Transportation",
          "Assisted Living Transportation"
        ],
        "knowsAbout": [
          "Non-Emergency Medical Transportation",
          "Wheelchair Van Transportation",
          "Dialysis Patient Transport",
          "Assisted Living Facility Partnerships",
          "Veteran Healthcare Transportation"
        ],
        "founder": {
          "@type": "Person",
          "name": "Guardian Veterans Team",
          "description": "Veteran-owned and operated medical transportation service"
        },
        "foundingDate": "2024",
        "slogan": "Serving Those Who Served",
        "sameAs": []
      },
      {
        "@type": "WebPage",
        "@id": `${import.meta.env.VITE_SITE_URL}/#webpage`,
        "url": `${import.meta.env.VITE_SITE_URL}/`,
        "name": "Guardian Veterans LLC NEMT | Medical Transportation West Valley Arizona",
        "description": "Guardian Veterans provides reliable wheelchair and ambulatory medical transportation for assisted living communities, dialysis clinics, and private patients in Sun City, Peoria, Surprise, Goodyear, and West Valley Arizona. Veteran-owned NEMT service launching soon.",
        "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL}/#business` },
        "inLanguage": "en-US",
        "dateModified": "2026-03-18"
      },
      {
        "@type": "FAQPage",
        "@id": `${import.meta.env.VITE_SITE_URL}/#faq`,
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What areas does Guardian Veterans LLC serve?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Guardian Veterans LLC provides medical transportation in West Valley Arizona, including Sun City, Peoria, Surprise, Goodyear, Scottsdale, Waddell, Avondale, Glendale, and surrounding communities."
            }
          },
          {
            "@type": "Question",
            "name": "What types of medical transportation does Guardian Veterans offer?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We offer wheelchair transportation, ambulatory transportation, dialysis transportation, hospital discharge rides, recurring medical appointment transport, and door-to-door assistance throughout West Valley Arizona."
            }
          },
          {
            "@type": "Question",
            "name": "Is Guardian Veterans LLC veteran-owned?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. Guardian Veterans LLC is a veteran-owned and operated Non-Emergency Medical Transportation (NEMT) company proudly serving the West Valley and Scottsdale Arizona communities."
            }
          },
          {
            "@type": "Question",
            "name": "When will Guardian Veterans NEMT service launch?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Guardian Veterans is launching soon. You can join the waitlist or fill out a facility survey to receive priority scheduling and preferred launch pricing."
            }
          },
          {
            "@type": "Question",
            "name": "How can assisted living facilities partner with Guardian Veterans?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Assisted living facilities can complete our Facility Transportation Survey on our website to become a founding partner and receive priority scheduling and preferred rates for medical transportation services."
            }
          },
          {
            "@type": "Question",
            "name": "What are Guardian Veterans\' business hours?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Guardian Veterans LLC is available 7 days a week from 6:30 AM to 5:30 PM, serving West Valley Arizona and surrounding areas for all non-emergency medical transportation needs."
            }
          },
          {
            "@type": "Question",
            "name": "How do I contact Guardian Veterans LLC?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "You can reach Guardian Veterans LLC by phone at (602) 295-6626, by email at info@gvnemt.com, or by filling out the contact form on our website. We are located at 9117 N 181st Ave, Waddell, AZ 85355."
            }
          }
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SeoHead
        title="Guardian Veterans LLC NEMT | Medical Transportation West Valley Arizona"
        description="Guardian Veterans provides reliable wheelchair and ambulatory medical transportation for assisted living communities, dialysis clinics, and private patients in Sun City, Peoria, Surprise, Goodyear, and West Valley Arizona. Veteran-owned NEMT service launching soon."
        canonical={`${import.meta.env.VITE_SITE_URL}/`}
        jsonLd={jsonLd}
      />
      <Hero />
      <Suspense fallback={<SectionFallback />}>
        <About />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <WhoWeServe />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Pricing />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <MeetTheTeam />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <InterestTracker />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <FacilityForm />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <PrivatePayForm />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Waitlist />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <NotifyMe />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <ContactUs />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Footer />
      </Suspense>
    </div>
  );
}
